import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Save, GripVertical, Eye, EyeOff, Layers } from 'lucide-react';
import { api } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';

const useEscape = (isOpen, onClose) => {
  useEffect(() => {
    if (!isOpen) return;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [isOpen, onClose]);
};

const emptyService = {
  title: '', subtitle: '', description: '', benefits: [], image_url: '', icon: 'target', sort_order: 0, active: true, duration_min: 60, default_price: 0,
};

export default function AdminServices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, mode: 'create', data: emptyService, id: null });
  const [confirm, setConfirm] = useState({ open: false, id: null, title: '' });
  useEscape(dialog.open, () => setDialog({ open: false, mode: 'create', data: emptyService, id: null }));
  useEscape(confirm.open, () => setConfirm({ open: false, id: null, title: '' }));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/services');
      setItems(data || []);
    } catch { toast.error('Failed to load services'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => setDialog({ open: true, mode: 'create', data: { ...emptyService, sort_order: (items[items.length - 1]?.sort_order || 0) + 1 }, id: null });
  const openEdit = (s) => setDialog({ open: true, mode: 'edit', data: { ...s, benefits: s.benefits || [] }, id: s.id });
  const close = () => setDialog({ open: false, mode: 'create', data: emptyService, id: null });

  const save = async (e) => {
    e?.preventDefault();
    const d = dialog.data;
    if (!d.title.trim() || !d.description.trim()) { toast.error('Title and description are required.'); return; }
    const payload = { ...d, benefits: (d.benefits || []).map((x) => (x || '').trim()).filter(Boolean) };
    try {
      if (dialog.mode === 'create') { await api.post('/admin/services', payload); toast.success('Service created.'); }
      else { await api.put(`/admin/services/${dialog.id}`, payload); toast.success('Service updated.'); }
      close(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
  };

  const del = async () => {
    try { await api.delete(`/admin/services/${confirm.id}`); toast.success('Deleted.'); setConfirm({ open: false, id: null, title: '' }); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const toggleActive = async (s) => {
    try { await api.put(`/admin/services/${s.id}`, { ...s, active: !s.active, benefits: s.benefits || [] }); load(); }
    catch { toast.error('Failed to update'); }
  };

  const move = async (s, dir) => {
    const idx = items.findIndex((x) => x.id === s.id);
    const other = items[idx + dir];
    if (!other) return;
    try {
      await Promise.all([
        api.put(`/admin/services/${s.id}`, { ...s, sort_order: other.sort_order, benefits: s.benefits || [] }),
        api.put(`/admin/services/${other.id}`, { ...other, sort_order: s.sort_order, benefits: other.benefits || [] }),
      ]);
      load();
    } catch { toast.error('Reorder failed'); }
  };

  return (
    <div>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl leading-tight tracking-tight inline-flex items-center gap-2"><Layers className="w-7 h-7 text-[var(--pg-admin-accent)]" /> Services &amp; Session Types</h1>
          <p className="mt-1 text-sm text-white/60 max-w-2xl">These are the offerings guests can book on the public site, and the session types available on the coaching calendar. Duration &amp; default price flow into both.</p>
          <p className="mt-1 text-sm text-white/60">Add, edit, reorder, and toggle services visible on your public site.</p>
        </div>
        <button
          data-testid={ADMIN.createServiceBtn}
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-[#F2B705] hover:bg-[#FFC83D] text-[#141414] font-semibold px-4 py-2.5 rounded-xl transition-colors"
        >
          <Plus className="w-4 h-4" /> New Service
        </button>
      </div>

      <div className="mt-6 rounded-2xl border border-white/10 bg-[#141414] overflow-hidden">
        {loading ? (
          <div className="p-6 text-white/60 text-sm">Loading...</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-white/60">
            <Layers className="w-8 h-8 mx-auto mb-3 text-white/30" />
            No services yet. Create your first one.
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table data-testid={ADMIN.servicesTable} className="admin-table min-w-[720px]">
            <thead>
              <tr>
                <th>Order</th>
                <th>Title</th>
                <th>Subtitle</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s, i) => (
                <tr key={s.id} data-testid={ADMIN.servicesRow(s.id)}>
                  <td>
                    <div className="inline-flex items-center gap-1">
                      <button aria-label="Move up" onClick={() => move(s, -1)} disabled={i === 0} className="p-1 rounded hover:bg-white/5 disabled:opacity-30"><GripVertical className="w-4 h-4" /></button>
                      <span className="text-white/70">{s.sort_order}</span>
                    </div>
                  </td>
                  <td className="font-semibold">{s.title}</td>
                  <td className="text-white/70">{s.subtitle || '—'}</td>
                  <td>
                    <button onClick={() => toggleActive(s)} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${s.active ? 'bg-[#F2B705]/15 text-[#F2B705] border-[#F2B705]/30' : 'bg-white/5 text-white/50 border-white/10'}`}>
                      {s.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />} {s.active ? 'Active' : 'Hidden'}
                    </button>
                  </td>
                  <td>
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openEdit(s)} data-testid={`admin-service-edit-${s.id}`} className="p-2 rounded-lg hover:bg-white/5 text-white/80" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => setConfirm({ open: true, id: s.id, title: s.title })} data-testid={`admin-service-delete-${s.id}`} className="p-2 rounded-lg hover:bg-red-500/10 text-red-300" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {dialog.open && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={close} />
          <form data-testid={ADMIN.serviceDialog} onSubmit={save} className="relative w-full max-w-2xl rounded-2xl border border-white/10 bg-[#141414] p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-2xl">{dialog.mode === 'create' ? 'New Service' : 'Edit Service'}</h3>
              <button type="button" onClick={close} aria-label="Close" className="p-2 rounded-lg hover:bg-white/5"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Title">
                <input data-testid={ADMIN.serviceTitleInput} value={dialog.data.title} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, title: e.target.value } })} className="pg-input" />
              </Field>
              <Field label="Subtitle">
                <input data-testid={ADMIN.serviceSubtitleInput} value={dialog.data.subtitle} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, subtitle: e.target.value } })} className="pg-input" />
              </Field>
              <Field label="Icon (lucide name)">
                <input data-testid={ADMIN.serviceIconInput} value={dialog.data.icon} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, icon: e.target.value } })} placeholder="target, users, graduation-cap..." className="pg-input" />
              </Field>
              <Field label="Sort Order">
                <input data-testid={ADMIN.serviceSortInput} type="number" value={dialog.data.sort_order} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, sort_order: parseInt(e.target.value || '0', 10) } })} className="pg-input" />
              </Field>
              <Field label="Duration (minutes)">
                <input type="number" min={15} max={600} value={dialog.data.duration_min ?? 60} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, duration_min: parseInt(e.target.value || '0', 10) } })} className="pg-input" />
              </Field>
              <Field label="Default price (SAR)">
                <input type="number" step="0.01" min={0} value={dialog.data.default_price ?? 0} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, default_price: parseFloat(e.target.value || '0') } })} className="pg-input" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Featured Image URL">
                  <input data-testid={ADMIN.serviceImageInput} value={dialog.data.image_url} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, image_url: e.target.value } })} placeholder="https://..." className="pg-input" />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Description">
                  <textarea data-testid={ADMIN.serviceDescInput} rows={3} value={dialog.data.description} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, description: e.target.value } })} className="pg-input resize-none" />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Benefits (one per line)">
                  <textarea data-testid={ADMIN.serviceBenefitsInput} rows={4} value={(dialog.data.benefits || []).join('\n')} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, benefits: e.target.value.split('\n') } })} className="pg-input resize-none" />
                </Field>
              </div>
              <label className="sm:col-span-2 inline-flex items-center gap-2 select-none cursor-pointer">
                <input data-testid={ADMIN.serviceActiveToggle} type="checkbox" checked={dialog.data.active} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, active: e.target.checked } })} className="w-4 h-4 accent-[#F2B705]" />
                <span className="text-sm">Active (visible on public site)</span>
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={close} className="px-4 py-2 rounded-xl border border-white/10 hover:bg-white/5">Cancel</button>
              <button data-testid={ADMIN.serviceSaveBtn} type="submit" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#F2B705] hover:bg-[#FFC83D] text-[#141414] font-semibold"><Save className="w-4 h-4" /> Save</button>
            </div>
            <style>{`.pg-input{width:100%;border:1px solid rgba(255,255,255,.1);background:rgba(0,0,0,.3);border-radius:.75rem;padding:.6rem .9rem;color:#fff;outline:none;transition:border-color .15s;}.pg-input:focus{border-color:#F2B705;box-shadow:0 0 0 3px rgba(242,183,5,.25);}`}</style>
          </form>
        </div>
      )}

      {confirm.open && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setConfirm({ open: false, id: null, title: '' })} />
          <div className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-[#141414] p-6">
            <h3 className="font-display text-2xl">Delete service?</h3>
            <p className="mt-2 text-sm text-white/60">This will permanently delete <span className="font-semibold text-white">{confirm.title}</span>. This cannot be undone.</p>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setConfirm({ open: false, id: null, title: '' })} className="px-4 py-2 rounded-xl border border-white/10 hover:bg-white/5">Cancel</button>
              <button onClick={del} data-testid="admin-service-delete-confirm" className="px-4 py-2 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/30">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-white/60 font-semibold">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
